/**
 * RevealTracker — Grid-based tracker for worm reveal progress.
 *
 * Divides the container into a grid of cells. When the player digs,
 * cells within the brush radius are marked as cleared. Each worm
 * tracks what fraction of its hit zone has been cleared.
 *
 * Pure math — no DOM, no React. Fully testable.
 */

import type { BuriedWorm } from '@/types';

const CELL_SIZE = 16; // px per grid cell

interface WormZone {
  worm: BuriedWorm;
  cellIndices: number[];   // Grid cell indices within hit radius
  totalCells: number;
  clearedCells: number;
  revealed: boolean;
}

export class RevealTracker {
  private readonly cols: number;
  private readonly rows: number;
  private readonly cleared: Uint8Array; // 0 = dirt, 1 = cleared
  private readonly wormZones: WormZone[];
  private readonly revealThreshold: number;

  constructor(
    width: number,
    height: number,
    worms: BuriedWorm[],
    wormHitRadius: number,
    revealThreshold: number,
  ) {
    this.cols = Math.ceil(width / CELL_SIZE);
    this.rows = Math.ceil(height / CELL_SIZE);
    this.cleared = new Uint8Array(this.cols * this.rows);
    this.revealThreshold = revealThreshold;

    // Pre-compute which grid cells fall within each worm's hit radius
    this.wormZones = worms.map((worm) => {
      const cellIndices: number[] = [];

      // Bounding box of the worm's hit zone in grid coords
      const minCol = Math.max(0, Math.floor((worm.x - wormHitRadius) / CELL_SIZE));
      const maxCol = Math.min(this.cols - 1, Math.floor((worm.x + wormHitRadius) / CELL_SIZE));
      const minRow = Math.max(0, Math.floor((worm.y - wormHitRadius) / CELL_SIZE));
      const maxRow = Math.min(this.rows - 1, Math.floor((worm.y + wormHitRadius) / CELL_SIZE));

      for (let row = minRow; row <= maxRow; row++) {
        for (let col = minCol; col <= maxCol; col++) {
          // Cell center position
          const cx = (col + 0.5) * CELL_SIZE;
          const cy = (row + 0.5) * CELL_SIZE;
          const dx = cx - worm.x;
          const dy = cy - worm.y;
          if (Math.sqrt(dx * dx + dy * dy) <= wormHitRadius) {
            cellIndices.push(row * this.cols + col);
          }
        }
      }

      return {
        worm,
        cellIndices,
        totalCells: Math.max(1, cellIndices.length), // prevent div by zero
        clearedCells: 0,
        revealed: false,
      };
    });
  }

  /**
   * Mark cells within brushRadius of (x, y) as cleared.
   * Returns array of worms that just crossed the reveal threshold.
   */
  dig(x: number, y: number, brushRadius: number): BuriedWorm[] {
    const newlyRevealed: BuriedWorm[] = [];

    // Mark grid cells within brush radius as cleared
    const minCol = Math.max(0, Math.floor((x - brushRadius) / CELL_SIZE));
    const maxCol = Math.min(this.cols - 1, Math.floor((x + brushRadius) / CELL_SIZE));
    const minRow = Math.max(0, Math.floor((y - brushRadius) / CELL_SIZE));
    const maxRow = Math.min(this.rows - 1, Math.floor((y + brushRadius) / CELL_SIZE));

    const newlyClearedIndices: number[] = [];

    for (let row = minRow; row <= maxRow; row++) {
      for (let col = minCol; col <= maxCol; col++) {
        const idx = row * this.cols + col;
        if (this.cleared[idx]) continue;

        const cx = (col + 0.5) * CELL_SIZE;
        const cy = (row + 0.5) * CELL_SIZE;
        const dx = cx - x;
        const dy = cy - y;
        if (Math.sqrt(dx * dx + dy * dy) <= brushRadius) {
          this.cleared[idx] = 1;
          newlyClearedIndices.push(idx);
        }
      }
    }

    // Update worm zones — only check zones that overlap with newly cleared cells
    if (newlyClearedIndices.length > 0) {
      const clearedSet = new Set(newlyClearedIndices);

      for (const zone of this.wormZones) {
        if (zone.revealed) continue;

        // Count how many of this zone's cells were just cleared
        for (const cellIdx of zone.cellIndices) {
          if (clearedSet.has(cellIdx)) {
            zone.clearedCells++;
          }
        }

        const revealPercent = zone.clearedCells / zone.totalCells;
        if (revealPercent >= this.revealThreshold) {
          zone.revealed = true;
          zone.worm.revealed = true;
          newlyRevealed.push(zone.worm);
        }
      }
    }

    return newlyRevealed;
  }

  /** Get reveal progress (0-1) for a specific worm */
  getRevealProgress(wormId: string): number {
    const zone = this.wormZones.find((z) => z.worm.id === wormId);
    if (!zone) return 0;
    return zone.clearedCells / zone.totalCells;
  }

  /** Get all revealed worms */
  getRevealedWorms(): BuriedWorm[] {
    return this.wormZones
      .filter((z) => z.revealed)
      .map((z) => z.worm);
  }

  /** Check if all worms are revealed */
  allRevealed(): boolean {
    return this.wormZones.every((z) => z.revealed);
  }
}
